import { memo, useEffect } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import {
  EmptyIndicator,
  FlagIcon,
  FlexContainer,
  LoadingIndicator,
  MoreOptions,
  TimeIndicator
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { DropdownButton } from 'components/common/dropdown';
import { EditorVersionHistoryUpdateTag } from '.';

import type { GetCoursePreviousVersionsRes } from 'shared-types/responses';

interface Props {
  onPagesChange: (pages: number) => void;
  onPreview: (url: string) => void;
  queryParams: string;
}

const EditorVersionHistoryTableCourse = ({
  onPagesChange,
  onPreview,
  queryParams
}: Props) => {
  const { dateNow: now } = useDateNow();

  const {
    language,
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetCoursePreviousVersionsRes>({
      url: `/admin/courses/${course.id}/versions-course-versions?${queryParams}`
    });

  useEffect(() => {
    if (!loading && data?.pages) onPagesChange(data?.pages || 1);
  }, [data, loading]);

  return loading ? (
    <LoadingIndicator />
  ) : !data?.previousVersions.length ? (
    <EmptyIndicator icon="history" title={messages.coursesNoVersions} />
  ) : (
    <BoxOverflowWrapper withStickyHeader={true}>
      <BoxTable>
        <BoxTableHeader>
          <BoxTableCell>{messages.commonTag}</BoxTableCell>
          <BoxTableCell>{messages.analyticsDateFrom}</BoxTableCell>
          <BoxTableCell>{messages.analyticsDateTo}</BoxTableCell>
          <BoxTableCell>{messages.commonChapters}</BoxTableCell>
          <BoxTableCell>{messages.courseLanguageVersionsDefault}</BoxTableCell>
          <BoxTableCell>{messages.courseLanguageVersions}</BoxTableCell>
          <BoxTableCell shrink={true} sticky={true} stickyAnchor="right" />
        </BoxTableHeader>

        {data?.previousVersions.map((version) => {
          const chapters = version.chapters || [];

          // calculate number of published and unpublished chapters
          let numberOfPublishedChapters = 0;
          let numberOfUnpublishedChapters = 0;
          chapters.forEach((chapter) => {
            if (
              chapter.publishDate &&
              new Date(chapter.publishDate).getTime() <= now
            ) {
              numberOfPublishedChapters++;
            } else {
              numberOfUnpublishedChapters++;
            }
          });

          // get info about language versions available
          const languageVersions = getCourseLanguageVersionOptions({
            course: version
          });

          return (
            <BoxTableRow key={version.id}>
              <BoxTableCell>
                <div style={{ minWidth: 100 }}>{version.tag || '-'}</div>
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                <span style={{ marginRight: 10 }}>
                  {getFormattedDate({
                    date: version.versionNumber,
                    dateFormat: 'dd.MM.yyyy HH:mm:ss'
                  })}
                </span>

                <span
                  style={{
                    color: theme.textColorLighter,
                    fontWeight: 400,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {'('}
                  <TimeIndicator
                    language={language}
                    timeString={version.versionNumber}
                  />
                  {')'}
                </span>
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {version.createdAt ? (
                  <>
                    <span style={{ marginRight: 10 }}>
                      {getFormattedDate({
                        date: version.createdAt,
                        dateFormat: 'dd.MM.yyyy HH:mm:ss'
                      })}
                    </span>

                    <span
                      style={{
                        color: theme.textColorLighter,
                        fontWeight: 400,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {'('}
                      <TimeIndicator
                        language={language}
                        timeString={version.createdAt}
                      />
                      {')'}
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        color: theme.textColorLighter,
                        fontWeight: 400,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {`(${messages.coursesCurrentVersion})`}
                    </span>
                  </>
                )}
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                <span style={{ marginRight: 10 }}>
                  {getMessageWithPlaceholders({
                    message: messages.courseNumberOfChapters,
                    placeholders: {
                      numberOfChapters: chapters.length
                    }
                  })}
                </span>
                {'('}
                <span className="published">
                  {getMessageWithPlaceholders({
                    message: messages.coursesNumberOfPublished,
                    placeholders: {
                      numberOfPublished: numberOfPublishedChapters
                    }
                  })}
                </span>
                {numberOfUnpublishedChapters > 0 && (
                  <>
                    {', '}
                    <span className="unpublished">
                      {getMessageWithPlaceholders({
                        message: messages.coursesNumberOfUnpublished,
                        placeholders: {
                          numberOfUnpublished: numberOfUnpublishedChapters
                        }
                      })}
                    </span>
                  </>
                )}
                {')'}
              </BoxTableCell>

              <BoxTableCell>
                <div className="language-version">
                  <FlagIcon lang={version.language} />
                  {languageVersions[version.language]?.capitalizedName}
                </div>
              </BoxTableCell>

              <BoxTableCell emptyValue="-">
                {Object.keys(languageVersions).length > 1 ? (
                  <FlexContainer>
                    {Object.keys(languageVersions)
                      .filter((language) => language !== version.language)
                      .map((language, i) => (
                        <div className="language-version" key={language}>
                          {i > 0 && <span>,&nbsp;</span>}
                          <FlagIcon lang={language} />
                          {languageVersions[language]?.capitalizedName}
                          {languageVersions[language].isPublished ? (
                            <span className="published">
                              {`(${messages.activitiesTextPublished})`}
                            </span>
                          ) : (
                            <span className="unpublished">
                              {`(${messages.activitiesTextUnpublished})`}
                            </span>
                          )}
                        </div>
                      ))}
                  </FlexContainer>
                ) : null}
              </BoxTableCell>

              <BoxTableCell
                sticky={true}
                stickyAnchor="right"
                stickyBackgroundColor="white"
              >
                <MoreOptions
                  renderAsFixed={true}
                  renderInPortal={true}
                  styleContentButtons={false}
                >
                  <EditorVersionHistoryUpdateTag
                    courseId={course.id}
                    currentTag={version.tag}
                    onUpdated={updateData}
                    versionNumber={
                      version.createdAt ? version.versionNumber : undefined
                    }
                  />

                  {version.createdAt ? (
                    <DropdownButton
                      category="light"
                      icon="eye"
                      onClick={() =>
                        onPreview(
                          `/admin/courses/${course.slug}` +
                            `/edit/${version.chapters[0].slug}` +
                            `?coursePreviewVersion=${version.versionNumber}`
                        )
                      }
                      text={messages.coursesPreviewInEditor}
                    />
                  ) : null}
                </MoreOptions>
              </BoxTableCell>
            </BoxTableRow>
          );
        })}
      </BoxTable>

      <style jsx>{`
        .published {
          color: ${theme.successColor};
        }
        .unpublished {
          color: ${theme.dangerLightTextColor};
        }
        .language-version {
          display: flex;
          align-items: center;
          gap: 5px;
        }
      `}</style>
    </BoxOverflowWrapper>
  );
};

export default memo(EditorVersionHistoryTableCourse);
